import {
  ClaimPlatform,
  completeClaimChallenge,
  createClaimChallenge,
  isWrappedErrorWithSignature,
  SignedBlockchainClaimChallenge,
} from './http'
import type { AxiosInstance } from 'axios'
import { createSimpleRegistrationError } from './errors'
import assert, { fail } from 'assert'
import {
  artBlocksIntegrationV1Abi,
  artBlocksIntegrationV2Abi,
  contractSovereignV5Abi,
  erc721Abi,
  grantRoleAbi,
  manifoldAbi,
  manifoldExtensionAbi,
  manifoldExtensionV2Abi,
  manifoldHasMintingPermissionAbi,
  highlightMintManagerAbi,
} from './abis/generated'
import {
  Account,
  Address,
  Chain,
  getAddress,
  Hex,
  keccak256,
  PublicClient,
  toBytes,
  Transport,
  WalletClient,
} from 'viem'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  useAccount,
  useDeployContract as useDeployContractWagmi,
  usePublicClient,
  useReadContract,
  useWriteContract,
} from 'wagmi'
import {
  base,
  baseSepolia,
  mainnet,
  polygon,
  polygonAmoy,
  sepolia,
} from 'viem/chains'
import { getGasLimit } from '@verisart/shared/src/utils/getGasLimit'
export enum Blockchain {
  POLYGON = 'POLYGON',
  POLYGON_AMOY = 'POLYGON_AMOY',
  ETHEREUM = 'ETHEREUM',
  ETHEREUM_SEPOLIA = 'ETHEREUM_SEPOLIA',
  FAKE_ETHEREUM_ON_SEPOLIA = 'FAKE_ETHEREUM_ON_SEPOLIA',
  FAKE_BASE_ON_BASE_SEPOLIA = 'FAKE_BASE_ON_BASE_SEPOLIA',
  BASE_MAINNET = 'BASE_MAINNET',
  BASE_SEPOLIA = 'BASE_SEPOLIA',

  // These are dead chains. They should only be used for rendering old certificates
  // {{{
  ETHEREUM_GOERLI = 'ETHEREUM_GOERLI',
  POLYGON_MUMBAI = 'POLYGON_MUMBAI',
  // }}}
}

/**
 * The overall type of chain or ecosystem, regardless of which actual chain.
 * This is needed because we now support testnets in both Polygon and Ethereum, but we treat
 * them differently (e.g. different Icons, gasless minting etc).
 */
export enum BlockchainClass {
  ETHEREUM = 'ETHEREUM',
  POLYGON = 'POLYGON',
  BASE = 'BASE',
}

export const GAS_COST_MINT = 288_549 // Worst case scenario: v3 of the contract with royalties set
export const GAS_COST_DEPLOY = 391_733 // V5

export function getMainChainId(blockchain: Blockchain): number {
  // Note that on test environment the mainnets actually still point to testnet
  if (
    blockchain === Blockchain.ETHEREUM_SEPOLIA ||
    blockchain == Blockchain.FAKE_ETHEREUM_ON_SEPOLIA
  )
    return sepolia.id
  if (blockchain === Blockchain.POLYGON_AMOY) return polygonAmoy.id
  if (
    blockchain === Blockchain.BASE_SEPOLIA ||
    blockchain === Blockchain.FAKE_BASE_ON_BASE_SEPOLIA
  )
    return baseSepolia.id
  if (blockchain === Blockchain.ETHEREUM) return mainnet.id
  if (blockchain === Blockchain.POLYGON) return polygon.id
  if (blockchain === Blockchain.BASE_MAINNET) return base.id
  throw Error(`Unknown blockchain ${blockchain}`)
}

/**
 * Converts a chain ID to its corresponding blockchain name
 * @param chainId The numeric chain ID
 * @returns The corresponding Blockchain enum value, or error if not found
 */
export function chainIdToBlockchain(chainId: number): Blockchain {
  switch (chainId) {
    case mainnet.id:
      return Blockchain.ETHEREUM
    case polygon.id:
      return Blockchain.POLYGON
    case sepolia.id:
      return Blockchain.ETHEREUM_SEPOLIA
    case polygonAmoy.id:
      return Blockchain.POLYGON_AMOY
    case base.id:
      return Blockchain.BASE_MAINNET
    case baseSepolia.id:
      return Blockchain.BASE_SEPOLIA
    default:
      throw new Error(`Unknown chain ID: ${chainId}`)
  }
}

export function openSeaAssetsBaseUrl(blockchain: Blockchain): string {
  // Note that on test environment the mainnets actually still point to testnet
  if (
    blockchain === Blockchain.ETHEREUM_SEPOLIA ||
    blockchain === Blockchain.FAKE_ETHEREUM_ON_SEPOLIA
  )
    return 'https://testnets.opensea.io/assets/sepolia'
  if (blockchain === Blockchain.POLYGON_AMOY)
    return 'https://testnets.opensea.io/assets/amoy'
  if (
    blockchain === Blockchain.BASE_SEPOLIA ||
    blockchain === Blockchain.FAKE_BASE_ON_BASE_SEPOLIA
  )
    return 'https://testnets.opensea.io/assets/base-sepolia'

  if (blockchain === Blockchain.POLYGON_MUMBAI)
    return 'https://testnets.opensea.io/assets/mumbai'
  if (blockchain === Blockchain.ETHEREUM)
    return 'https://opensea.io/assets/ethereum'
  if (blockchain === Blockchain.POLYGON)
    return 'https://opensea.io/assets/matic'
  if (blockchain === Blockchain.BASE_MAINNET)
    return 'https://opensea.io/assets/base'
  if (blockchain === Blockchain.ETHEREUM_GOERLI)
    return 'https://opensea.io/assets/goerli'
  throw Error(`Unknown blockchain ${blockchain}`)
}

// See RegistrationErrorCause
export const translations = {
  no_wallet_found: 'No wallet found',
  already_linked: 'Wallet already linked',
  already_claimed_by_other:
    'This wallet is linked to a different email address',
  metamask_already_pending: 'Metamask has a pending transaction',
  metamask_user_cancelled: 'User cancelled transaction',
  unknown: 'Error occurred when linking wallet. Technical information: ',
}

export function scannerBaseUrl(blockchain: Blockchain): string {
  // Note that on test environment the mainnets actually still point to testnet
  if (
    blockchain === Blockchain.ETHEREUM_SEPOLIA ||
    blockchain === Blockchain.FAKE_ETHEREUM_ON_SEPOLIA
  )
    return 'https://sepolia.etherscan.io'
  if (blockchain === Blockchain.POLYGON_AMOY)
    return 'https://amoy.polygonscan.com'
  if (
    blockchain === Blockchain.BASE_SEPOLIA ||
    blockchain === Blockchain.FAKE_BASE_ON_BASE_SEPOLIA
  )
    return 'https://sepolia.basescan.org'

  if (blockchain === Blockchain.POLYGON_MUMBAI)
    return 'https://mumbai.polygonscan.com'
  if (blockchain === Blockchain.ETHEREUM) return 'https://etherscan.io'
  if (blockchain === Blockchain.POLYGON) return 'https://polygonscan.com'
  if (blockchain === Blockchain.BASE_MAINNET) return 'https://basescan.org'
  if (blockchain === Blockchain.ETHEREUM_GOERLI)
    return 'https://goerli.etherscan.io'
  throw Error(`Unknown blockchain ${blockchain}`)
}

/** See BlockchainClass */
export function blockchainToClass(blockchain: Blockchain): BlockchainClass {
  return blockchain === Blockchain.ETHEREUM_SEPOLIA ||
    blockchain === Blockchain.FAKE_ETHEREUM_ON_SEPOLIA ||
    blockchain === Blockchain.ETHEREUM_GOERLI ||
    blockchain === Blockchain.ETHEREUM
    ? BlockchainClass.ETHEREUM
    : blockchain === Blockchain.POLYGON_MUMBAI ||
      blockchain === Blockchain.POLYGON ||
      blockchain === Blockchain.POLYGON_AMOY
    ? BlockchainClass.POLYGON
    : blockchain === Blockchain.BASE_MAINNET ||
      blockchain === Blockchain.FAKE_BASE_ON_BASE_SEPOLIA ||
      blockchain === Blockchain.BASE_SEPOLIA
    ? BlockchainClass.BASE
    : fail('Unknown blockchain')
}

export function isTestnet(blockchain: Blockchain) {
  return (
    blockchain === Blockchain.ETHEREUM_GOERLI ||
    blockchain === Blockchain.ETHEREUM_SEPOLIA ||
    blockchain === Blockchain.POLYGON_MUMBAI ||
    blockchain === Blockchain.POLYGON_AMOY ||
    blockchain === Blockchain.BASE_SEPOLIA
  )
}

export type SimpleRegistrationErrorCause =
  | 'no_wallet_found'
  | 'already_linked'
  | 'metamask_already_pending'
  | 'metamask_user_cancelled'
  | 'magic_user_cancelled'

export interface SimpleRegistrationError extends Error {
  type: 'simple_registration_error'
  cause: SimpleRegistrationErrorCause
  isRegistrationError: boolean
}

export interface WalletLinkedOtherComplexRegistrationError extends Error {
  type: 'complex_registration_error'
  cause: 'already_claimed_by_other'
  isRegistrationError: boolean
  misc: {
    detailedMessage: string
    signedChallenge: SignedBlockchainClaimChallenge
  }
}

export function isConnectWalletError(err: unknown): err is ConnectWalletError {
  if (!err) return false
  return (
    isRegistrationError(err) || isWalletLinkedOtherComplexRegistrationError(err)
  )
}

export type ConnectWalletError =
  | SimpleRegistrationError
  | WalletLinkedOtherComplexRegistrationError

export function isWalletLinkedOtherComplexRegistrationError(
  err: unknown
): err is WalletLinkedOtherComplexRegistrationError {
  return (
    (err as any).cause === 'already_claimed_by_other' &&
    (err as any).type === 'complex_registration_error'
  )
}

export const isRegistrationError = (
  err: unknown
): err is SimpleRegistrationError => {
  return (
    ((err as any).isRegistrationError ?? false) &&
    ((err as any).type === 'simple_registration_error' ||
      (err as any).type === 'complex_registration_error')
  )
}

const emailPattern = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g

const already_claimed_by_other_recovery_translation = (
  ownedBy: string,
  changeTo: string
) =>
  `This wallet is already associated to an existing email "${ownedBy}". Would you like to move this wallet to the email address "${changeTo}"?`
/**
 * Links/registers a wallet with the user's account on core
 *
 * Throws exceptions in the form defined by
 *
 * @see createRegistrationError
 */
export async function registerWallet(
  webClient: AxiosInstance,
  walletClient: WalletClient<Transport, Chain, Account>,
  moveExisting: boolean,
  claimPlatform: ClaimPlatform,
  shopDomain?: string
): Promise<void> {
  try {
    const claimChallenge = await createClaimChallenge(
      webClient,
      walletClient.account.address,
      claimPlatform,
      shopDomain
    )
    if (claimChallenge) {
      const { challenge, accountCommitment } = claimChallenge
      const signature = await walletClient.signMessage({
        message: challenge,
      })
      await completeClaimChallenge(webClient, {
        accountCommitment,
        address: walletClient.account.address,
        challenge,
        signature,
        moveExisting,
      })
    } else {
      throw createSimpleRegistrationError('already_linked')
    }
  } catch (error: any) {
    const { e, signedChallenge } = isWrappedErrorWithSignature(error)
      ? { e: error.wrappedError, signedChallenge: error.signedChallenge }
      : { e: error, signedChallenge: undefined }
    if (!isRegistrationError(e)) {
      if (e.message?.includes('already pending')) {
        throw createSimpleRegistrationError('metamask_already_pending')
      }
      if (
        e.message?.includes('User denied') ||
        e.message?.includes('User cancelled')
      ) {
        throw createSimpleRegistrationError('metamask_user_cancelled')
      }

      if (
        e.response?.data?.message?.includes('already.claimed.other') ||
        e.data?.message?.includes('already.claimed.other')
      ) {
        assert(signedChallenge, 'signedChallenge should be defined here')
        const receivedErrorMessage =
          e.response?.data?.message ?? e.data?.message
        let errorMsg: string =
          translations['already_claimed_by_other' as keyof typeof translations]
        if (receivedErrorMessage) {
          const emailAddresses = receivedErrorMessage.match(emailPattern)
          if (emailAddresses && emailAddresses.length === 2) {
            errorMsg = already_claimed_by_other_recovery_translation(
              emailAddresses?.[1],
              emailAddresses?.[0]
            )
          }
        } else {
          console.error(
            "Expected to receive error message from server but didn't"
          )
          throw e
        }
        // For reasons, website wraps axios errors with the response object so we need to look there too
        const error: WalletLinkedOtherComplexRegistrationError = {
          name: 'RegistrationError',
          message: translations['already_claimed_by_other'],
          type: 'complex_registration_error',
          cause: 'already_claimed_by_other',
          isRegistrationError: true,
          misc: {
            detailedMessage: errorMsg,
            signedChallenge,
          },
        }
        throw error
      }

      if (e.error?.code === 4001) {
        if (e.error?.message === 'A request is already in progress') {
          throw createSimpleRegistrationError('metamask_already_pending')
        }
        throw createSimpleRegistrationError('metamask_user_cancelled')
      }
      throw e
    }
    throw e
  }
}

/**
 * Deploys a contract and returns a promise for the *transaction accepted* (rather than the transaction complete)
 */
export const useDeployContract = () => {
  const { deployContractAsync } = useDeployContractWagmi()
  const publicClient = usePublicClient()
  const { address } = useAccount()

  return useMutation({
    mutationFn: async (data: {
      abi: any
      bytecode: Hex
      args: any[]
      gas?: number
    }) => {
      if (!publicClient || !address) {
        throw new Error('No wallet client or address')
      }

      const tx = await deployContractAsync({
        account: address,
        abi: data.abi as any,
        bytecode: data.bytecode,
        args: data.args,
        gas: data.gas
          ? getGasLimit(
              chainIdToBlockchain(publicClient.chain.id),
              BigInt(Math.floor(data.gas * 1.3)) // fee estimate to complete the transaction
            )
          : undefined,
      })

      return publicClient.waitForTransactionReceipt({ hash: tx })
    },
  })
}

/**
 * Value of keccak256("MINTER_ROLE")
 */
const MINTER_ROLE = keccak256(toBytes('MINTER_ROLE'))

/**
 * If the user can grant or revoke minting permission on a Verisart style contract
 * (using OpenZeppelin access control). This works for all versions of Verisart contracts as the
 * ABI has not changed for those functions.
 *
 * @param client
 * @param userAddress
 * @param contractAddress
 */

export const canGrantOrRevokeMintingPermission = async (
  client: PublicClient,
  address: Address,
  contractAddress: Address
) => {
  return await client.readContract({
    address: contractAddress,
    abi: grantRoleAbi,
    functionName: 'hasRole',
    args: [
      '0x0000000000000000000000000000000000000000000000000000000000000000',
      address,
    ],
  })
}

export const useCanGrantOrRevokeMintingPermission = (
  contractAddress: Address
) => {
  const { address } = useAccount()
  const client = usePublicClient()
  return useQuery({
    queryKey: [
      'canGrantOrRevokeMintingPermission',
      contractAddress,
      address,
      client,
    ],
    queryFn: () => {
      if (!client || !address) {
        return false
      }

      return canGrantOrRevokeMintingPermission(client, address, contractAddress)
    },
    enabled: !!address,
  })
}

export const useOwnerOf721 = (contractAddress: Address, tokenId: string) => {
  return useReadContract({
    address: contractAddress,
    abi: erc721Abi,
    functionName: 'ownerOf',
    args: [BigInt(tokenId)],
  })
}

export const useGetApproved = (contractAddress: Address, tokenId: string) => {
  return useReadContract({
    address: contractAddress,
    abi: erc721Abi,
    functionName: 'getApproved',
    args: [BigInt(tokenId)],
  })
}

export async function isApprovedForAll(
  publicClient: PublicClient,
  contractAddress: Address,
  ownerAddress: Address,
  operatorAddress: Address
): Promise<boolean> {
  return await publicClient.readContract({
    address: contractAddress,
    abi: erc721Abi,
    functionName: 'isApprovedForAll',
    args: [ownerAddress, operatorAddress],
  })
}

export const useIsApprovedForAll = (
  contractAddress: Address,
  ownerAddress?: Address,
  operatorAddress?: Address
) => {
  return useReadContract({
    address: contractAddress,
    abi: erc721Abi,
    functionName: 'isApprovedForAll',
    args:
      !!ownerAddress && !!operatorAddress
        ? [ownerAddress, operatorAddress]
        : undefined,
    query: {
      enabled: !!ownerAddress && !!operatorAddress,
    },
  })
}

export const useSafeTransferFrom721 = () => {
  const { address: walletAddress } = useAccount()
  const { writeContractAsync: writeContract } = useWriteContract()
  const publicClient = usePublicClient()

  return useMutation({
    mutationFn: async (data: {
      contractAddress: Address
      tokenId: string
      toAddress: Address
    }) => {
      const { contractAddress, tokenId, toAddress } = data

      if (!walletAddress) {
        throw new Error('No active wallet found')
      }
      if (!publicClient) {
        throw new Error('No public client found')
      }

      const tx = await writeContract({
        address: contractAddress,
        abi: erc721Abi,
        functionName: 'safeTransferFrom',
        args: [walletAddress, toAddress, BigInt(tokenId)],
      })

      return publicClient.waitForTransactionReceipt({ hash: tx })
    },
  })
}

export const useGrantMintingPermission = () => {
  const { writeContractAsync: writeContract } = useWriteContract()
  const publicClient = usePublicClient()

  return useMutation({
    mutationFn: async (data: {
      contractAddress: Address
      grantPermissionTo: Address
    }) => {
      const { contractAddress, grantPermissionTo } = data

      if (!publicClient) {
        throw new Error('No public client found')
      }

      const tx = await writeContract({
        address: contractAddress,
        abi: grantRoleAbi,
        functionName: 'grantRole',
        args: [MINTER_ROLE, grantPermissionTo],
      })
      return publicClient.waitForTransactionReceipt({ hash: tx })
    },
  })
}

export const useRevokeMintingPermission = () => {
  const { writeContractAsync: writeContract } = useWriteContract()
  const publicClient = usePublicClient()

  return useMutation({
    mutationFn: async (data: {
      contractAddress: Address
      revokePermissionFrom: Address
    }) => {
      const { contractAddress, revokePermissionFrom } = data

      if (!publicClient) {
        throw new Error('No public client found')
      }

      const tx = await writeContract({
        address: contractAddress,
        abi: grantRoleAbi,
        functionName: 'revokeRole',
        args: [MINTER_ROLE, revokePermissionFrom],
      })
      return publicClient.waitForTransactionReceipt({ hash: tx })
    },
  })
}

export const useManifoldExtensionGrantMintingPermission = () => {
  const { writeContractAsync: writeContract } = useWriteContract()
  const publicClient = usePublicClient()

  return useMutation({
    mutationFn: async (data: {
      extensionAddress: Address
      manifoldContractAddress: Address
      grantPermissionTo: Address
    }) => {
      const { extensionAddress, manifoldContractAddress, grantPermissionTo } =
        data

      if (!publicClient) {
        throw new Error('No public client found')
      }

      const tx = await writeContract({
        address: extensionAddress,
        abi: manifoldExtensionAbi,
        functionName: 'grantMinting',
        args: [manifoldContractAddress, grantPermissionTo],
      })
      return publicClient.waitForTransactionReceipt({ hash: tx })
    },
  })
}

export const useManifoldExtensionRevokeMintingPermission = () => {
  const { writeContractAsync: writeContract } = useWriteContract()
  const publicClient = usePublicClient()

  return useMutation({
    mutationFn: async (data: {
      extensionAddress: Address
      manifoldContractAddress: Address
      revokePermissionFrom: Address
    }) => {
      const {
        extensionAddress,
        manifoldContractAddress,
        revokePermissionFrom,
      } = data

      if (!publicClient) {
        throw new Error('No public client found')
      }

      const tx = await writeContract({
        address: extensionAddress,
        abi: manifoldExtensionAbi,
        functionName: 'revokeMinting',
        args: [manifoldContractAddress, revokePermissionFrom],
      })
      return publicClient.waitForTransactionReceipt({ hash: tx })
    },
  })
}

export const useMintHighlightChooseSignableTransaction = () => {
  const { writeContractAsync: writeContract } = useWriteContract()
  const publicClient = usePublicClient()
  return useMutation({
    mutationFn: async (data: {
      mintManagerAddress: Address
      mintTo: Address
      mechanicVectorId: Hex
      highlightChosenTokens: Address[]
      signature: Hex
      highlightMintFee: bigint
    }) => {
      const {
        mintManagerAddress,
        mintTo,
        mechanicVectorId,
        highlightChosenTokens,
        signature,
        highlightMintFee,
      } = data

      if (!publicClient) {
        throw new Error('No public client found')
      }

      const tx = await writeContract({
        address: mintManagerAddress,
        abi: highlightMintManagerAbi,
        functionName: 'mechanicMintChoose',
        args: [
          mechanicVectorId,
          mintTo,
          highlightChosenTokens.map((it) => BigInt(it)),
          signature,
        ],
        value: highlightMintFee * BigInt(highlightChosenTokens.length),
      })
      return publicClient.waitForTransactionReceipt({ hash: tx })
    },
  })
}

export const useMintHighlightSignableTransaction = () => {
  const { writeContractAsync: writeContract } = useWriteContract()
  const publicClient = usePublicClient()
  return useMutation({
    mutationFn: async (data: {
      mintManagerAddress: Address
      mintTo: Address
      mechanicVectorId: Hex
      numToMint: number
      signature: Hex
      highlightMintFee: bigint
    }) => {
      const {
        mintManagerAddress,
        mintTo,
        mechanicVectorId,
        numToMint,
        signature,
        highlightMintFee,
      } = data

      if (!publicClient) {
        throw new Error('No public client found')
      }

      const tx = await writeContract({
        address: mintManagerAddress,
        abi: highlightMintManagerAbi,
        functionName: 'mechanicMintNum',
        args: [mechanicVectorId, mintTo, numToMint, signature],
        value: highlightMintFee * BigInt(numToMint),
      })
      return publicClient.waitForTransactionReceipt({ hash: tx })
    },
  })
}

export const useMintArtblocksSignableTransaction = () => {
  const { writeContractAsync: writeContract } = useWriteContract()
  const publicClient = usePublicClient()

  return useMutation({
    mutationFn: async (data: {
      minterContractAddress: Address
      mintTo: Address
      projectId: number
      version: '1.0' | '2.0'
      artBlocksHash: Hex | null
      coreContractAddress: Address
      tokenNonce: Hex
      signature: Hex
    }) => {
      const {
        minterContractAddress,
        mintTo,
        projectId,
        version,
        artBlocksHash,
        coreContractAddress,
        tokenNonce,
        signature,
      } = data

      if (!publicClient) {
        throw new Error('No public client found')
      }

      if (version == '1.0') {
        assert(!artBlocksHash, 'artBlocksHash is only supported in version 2.0')
        const tx = await writeContract({
          address: minterContractAddress,
          abi: artBlocksIntegrationV1Abi,
          functionName: 'mintSigned',
          args: [
            mintTo,
            BigInt(projectId),
            coreContractAddress,
            tokenNonce,
            signature,
          ],
        })
        return publicClient.waitForTransactionReceipt({ hash: tx })
      } else {
        if (!artBlocksHash) {
          const tx = await writeContract({
            address: minterContractAddress,
            abi: artBlocksIntegrationV2Abi,
            functionName: 'mintSigned',
            args: [
              mintTo,
              BigInt(projectId),
              coreContractAddress,
              tokenNonce,
              signature,
            ],
          })
          return publicClient.waitForTransactionReceipt({ hash: tx })
        } else {
          const tx = await writeContract({
            address: minterContractAddress,
            abi: artBlocksIntegrationV2Abi,
            functionName: 'mintSignedHash',
            args: [
              mintTo,
              BigInt(projectId),
              coreContractAddress,
              artBlocksHash,
              signature,
            ],
          })
          return publicClient.waitForTransactionReceipt({ hash: tx })
        }
      }
    },
  })
}

export const useMintManifoldSignableTransaction = () => {
  const { writeContractAsync: writeContract } = useWriteContract()
  const publicClient = usePublicClient()

  return useMutation({
    mutationFn: async (data: {
      creatorContractAddress: Address
      extensionAddress: Address
      mintTo: Address
      uri: string
      tokenNonce: Hex
      royaltyAddresses: Address[]
      royaltyBpss: number[]
      signature: Hex
    }) => {
      const {
        creatorContractAddress,
        extensionAddress,
        mintTo,
        uri,
        tokenNonce,
        royaltyAddresses,
        royaltyBpss,
        signature,
      } = data

      if (!publicClient) {
        throw new Error('No public client found')
      }

      const tx = await writeContract({
        address: extensionAddress,
        abi: manifoldExtensionV2Abi,
        functionName: 'mintSigned',
        args: [
          mintTo,
          creatorContractAddress,
          uri,
          tokenNonce,
          royaltyAddresses,
          royaltyBpss.map((it) => BigInt(it)),
          signature,
        ],
      })
      return publicClient.waitForTransactionReceipt({ hash: tx })
    },
  })
}

export const useMintSignableTransaction = () => {
  const { writeContractAsync: writeContract } = useWriteContract()
  const publicClient = usePublicClient()

  return useMutation({
    mutationFn: async (data: {
      contractAddress: Address
      mintTo: Address
      uri: string
      tokenNonce: Hex
      royaltyAddresses: Address[]
      royaltyBpss: number[]
      signature: Hex
    }) => {
      const {
        contractAddress,
        mintTo,
        uri,
        tokenNonce,
        royaltyAddresses,
        royaltyBpss,
        signature,
      } = data
      if (!publicClient) {
        throw new Error('No public client found')
      }

      const tx = await writeContract({
        address: contractAddress,
        abi: contractSovereignV5Abi,
        functionName: 'mintSigned',
        args: [
          mintTo,
          uri,
          tokenNonce,
          royaltyAddresses,
          royaltyBpss.map((it) => BigInt(it)),
          signature,
        ],
      })
      return publicClient.waitForTransactionReceipt({ hash: tx })
    },
  })
}

async function manifoldExtensionHasMintingPermission(
  client: PublicClient,
  account: Address,
  extensionAddress: Address,
  manifoldContractAddress: Address
): Promise<boolean> {
  return await client.readContract({
    address: extensionAddress,
    abi: manifoldHasMintingPermissionAbi,
    functionName: 'hasMintingPermission',
    args: [manifoldContractAddress, account],
  })
}

export const useManifoldExtensionHasMintingPermission = (
  extensionAddress: Address,
  manifoldContractAddress: Address
) => {
  const { address } = useAccount()
  const client = usePublicClient()

  return useQuery({
    queryKey: [
      'manifoldExtensionHasMintingPermission',
      extensionAddress,
      manifoldContractAddress,
      address,
      client,
    ],
    queryFn: () => {
      if (!client || !address) {
        return false
      }
      return manifoldExtensionHasMintingPermission(
        client,
        address,
        extensionAddress,
        manifoldContractAddress
      )
    },
    enabled: !!address && !!client,
  })
}

export const manifoldIsAdmin = async (
  client: PublicClient,
  account: Address,
  contractAddress: Address
) => {
  return await client.readContract({
    address: contractAddress,
    abi: manifoldAbi,
    functionName: 'isAdmin',
    args: [account],
  })
}

export const useManifoldIsAdmin = (contractAddress: Address) => {
  const { address } = useAccount()
  const client = usePublicClient()
  return useQuery({
    queryKey: ['manifoldIsAdmin', contractAddress, address, client],
    queryFn: () => {
      if (!client || !address) {
        return false
      }

      return manifoldIsAdmin(client, address, contractAddress)
    },
    enabled: !!address && !!client,
  })
}

export const manifoldGetExtensions = (
  client: PublicClient,
  contractAddress: Address
) => {
  return client.readContract({
    address: contractAddress,
    abi: manifoldAbi,
    functionName: 'getExtensions',
  }) as Promise<Address[]>
}

export const useManifoldGetExtensions = (contractAddress: Address) => {
  const client = usePublicClient()
  return useQuery({
    queryKey: ['manifoldGetExtensions', contractAddress, client],
    queryFn: () => {
      if (!client) {
        return []
      }
      return manifoldGetExtensions(client, contractAddress)
    },
    enabled: !!client,
  })
}

export const useManifoldRegisterExtension = () => {
  const { writeContractAsync: writeContract } = useWriteContract()
  const client = usePublicClient()
  return useMutation({
    mutationFn: async (data: {
      extensionAddress: Address
      baseUri: string
      contractAddress: Address
    }) => {
      const { extensionAddress, baseUri, contractAddress } = data

      if (!client) {
        throw new Error('No public client found')
      }

      const tx = await writeContract({
        address: contractAddress,
        abi: manifoldAbi,
        functionName: 'registerExtension',
        args: [extensionAddress, baseUri],
      })
      return client.waitForTransactionReceipt({ hash: tx })
    },
  })
}

export const manifoldIsExtensionRegistered = async (
  client: PublicClient,
  extensionAddress: Address,
  contractAddress: Address
) => {
  const extensions = await manifoldGetExtensions(client, contractAddress)

  return (
    extensions
      ?.map((it) => getAddress(it))
      .includes(getAddress(extensionAddress)) ?? false
  )
}

export const useManifoldIsExtensionRegistered = (
  extensionAddress: Address,
  contractAddress: Address
) => {
  const client = usePublicClient()

  return useQuery({
    queryKey: [
      'manifoldIsExtensionRegistered',
      extensionAddress,
      contractAddress,
      client,
    ],
    queryFn: () => {
      if (!client) {
        return false
      }
      return manifoldIsExtensionRegistered(
        client,
        extensionAddress,
        contractAddress
      )
    },
    enabled: !!client,
  })
}

export const manifoldCanMintOnExtension = async (
  client: PublicClient,
  account: Address,
  extensionAddress: Address,
  contractAddress: Address
) => {
  return (
    (await manifoldIsExtensionRegistered(
      client,
      extensionAddress,
      contractAddress
    )) &&
    (await manifoldExtensionHasMintingPermission(
      client,
      account,
      extensionAddress,
      contractAddress
    ))
  )
}

export const useManifoldCanMintOnExtension = (
  extensionAddress: Address,
  contractAddress: Address
) => {
  const { address } = useAccount()
  const client = usePublicClient()

  return useQuery({
    queryKey: [
      'manifoldCanMintOnExtension',
      extensionAddress,
      contractAddress,
      address,
      client,
    ],
    queryFn: () => {
      if (!client || !address) {
        return false
      }

      return manifoldCanMintOnExtension(
        client,
        address,
        extensionAddress,
        contractAddress
      )
    },
    enabled: !!client && !!address,
  })
}

export const ErrorMetamaskUnknownChain = 4902

export const sleep = <T>(
  delay: number,
  resolveValue?: T
): Promise<T | undefined> =>
  new Promise((resolve) => {
    setTimeout(() => resolve(resolveValue), delay)
  })

export type ContractType =
  | 'REGULAR'
  | 'DROP'
  | 'RESERVED_TOKEN_DROP'
  | 'MANIFOLD_EXTENSION'
  | 'MANIFOLD'
  | 'ART_BLOCKS'
  | 'HIGHLIGHT'
